import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { isEqual } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';

import { ShFormGroupComponent } from '../../../form-controls/components/_core/form-group/form-group.component';
import { ShIndeterminateDirective } from '../../../form-controls/directives/indeterminate.directive';
import { DropdownDirection as DroppanelOpenDirection } from '../../../form-controls/entities/dropdown-direction.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ClearMarginModifiers } from '../../entities/clear-margin-modifier.entity';
import { ArrowNavigationHelper } from '../../helpers/arrow-navigation.helper';
import { ShActionItemComponent } from '../action-item/action-item.component';
import { ActionItem, ActionItemModifier, ActionItemStyle } from '../action-item/entities/action-item.entity';
import { ShDroppanelComponent } from '../droppanel/droppanel.component';
import { DroppanelStyle } from '../droppanel/entities/droppanel-style.entity';
import { IconSize } from '../icon/entities/icon-sizes.entity';
import { ShIconModule } from '../icon/icon.module';
import { ShChildIndexDirective } from '../../directives/child-index/child-index';

@Component({
    standalone: true,
    selector: 'sh-live-options-select',
    templateUrl: './live-options-select.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShIconModule,
        ShDroppanelComponent,
        ShIndeterminateDirective,
        ShFormGroupComponent,
        ShActionItemComponent,
        ShChildIndexDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShLiveOptionsSelectComponent<T = string>
    extends BaseContainer
    implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
    private readonly OPTION_QUERY: string = '.sh-droppanel__list-action:not(.-sh-disabled)';
    /* Base sh-droppanel properties */
    public readonly limitHeightToViewport$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /* Selection properties */
    private selectedState$!: Observable<FormValue<FormSelectOption<T>>[]>;

    readonly initialSelectedValues$: ReplaySubject<FormValue<T>[]> = new ReplaySubject<FormValue<T>[]>();
    readonly state$: BehaviorSubject<FormValue<FormSelectOption<T>>[]> = new BehaviorSubject<
        FormValue<FormSelectOption<T>>[]
    >([]);

    public selectedValues$!: Observable<T[]>;
    public showExtraItemsLabelIndicator$!: Observable<boolean>;
    public selectLabel$: Observable<string>;

    /* Search properties */
    public searchTerm$!: Observable<string>;
    public searchControl: UntypedFormControl;

    /* State */
    public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    /* Options */
    private readonly multiFormSelectOptions$: ReplaySubject<MultiFormSelectOptions<T>> = new ReplaySubject<
        MultiFormSelectOptions<T>
    >(1);

    public options$: BehaviorSubject<FormSelectOption<T>[]> = new BehaviorSubject<FormSelectOption<T>[]>([]);
    public hasOptions$!: Observable<boolean>;
    public currentPage$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /* Delete */
    readonly deletePress$: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();

    private readonly DELETE_INPUT_KEYS: string[] = ['Backspace', 'Delete', 'Clear'];

    /* Others */
    public sizes: typeof IconSize = IconSize;
    public states: typeof FormValueState = FormValueState;
    public clearMarginModifiers: ClearMarginModifiers = ClearMarginModifiers.CLEAR_MARGIN_TOP_IMPORTANT;

    private arrowNavigation: ArrowNavigationHelper;

    // Injectors
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    @HostListener('window:scroll', ['$event'])
    onWindowScroll(event: Event) {
        this.setDropdownClosed();
    }

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        this.arrowNavigation.handleKeyDown({
            event,
            optionContainerElement: this.optionContainerElement,
            toggleContainer: this.toggleContainer,
            searchElement: this.searchElement,
            closeDropdown: () => this.setDropdownClosed()
        });
    }

    /* Messages */
    @Input() label: string;
    @Input() placeholderMsg: string;
    @Input() noResultsFoundMsg: string = this.translateService.translate('ui.no-results');
    @Input() searchPlaceholderMsg: string = this.translateService.translate('ui.search');
    @Input() showMoreMsg = this.translateService.translate('ui.load-more');
    @Input() loadingMsg = this.translateService.translate('ui.loading');

    /* Base sh-droppanel inputs */
    @Input() inOverflownContainer = false;
    @Input() height = 300;
    @Input() openDirection: DroppanelOpenDirection = DroppanelOpenDirection.Below;
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;

    @Input() set limitHeightToViewport(limitHeightToViewport: boolean) {
        this.limitHeightToViewport$.next(limitHeightToViewport);
    }

    /* Specific sh-droppanel-select inputs */
    @Input() id?: string = 'droppanel';
    @Input() search = false;
    @Input() multiple = false;
    @Input() allowSingleDeselect = true;
    @Input() resultsPerPage = 50;
    @Input() sortAlphabetically = false;

    /* Paginated entity functions */
    @Input() paginatedResultsFct!: (term: string, pagination: PaginationInfo) => Observable<PaginatedResults<T>>;
    @Input() valueToLabelFct!: (value: T) => string;

    @Input() set initialSelectedValues(values: FormValue<T>[]) {
        if (values) {
            this.initialSelectedValues$.next(values);
        }
    }

    @Output() selectionState: EventEmitter<T[]> = new EventEmitter<T[]>();

    @ViewChild('toggleContainer') toggleContainer: ElementRef<HTMLElement>;
    @ViewChild('searchInput') searchElement?: ElementRef<HTMLElement>;
    @ViewChild('optionContainer') optionContainerElement?: ElementRef<HTMLElement>;

    constructor(private readonly formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.initArrowNavigation();
        this.initControls();
        this.initSourceStreams();
        this.initStateStreams();
        this.initMessageStream();

        this.initOptionsStream();

        this.initListeners();
        this.initHasOptionsStream();
    }

    ngAfterViewInit(): void {
        this.initStateChangeListener();
    }

    ngAfterViewChecked() {
        this.arrowNavigation.manageFocus(this.isOpen$.value, this.optionContainerElement, this.searchElement);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.limitHeightToViewport$.complete();

        this.multiFormSelectOptions$.complete();
        this.state$.complete();

        this.deletePress$.complete();
    }

    private initArrowNavigation(): void {
        this.arrowNavigation = new ArrowNavigationHelper(
            this.cdr,
            this.isOpen$,
            this.destroy$,
            this.OPTION_QUERY,
            true
        );
    }

    private initControls(): void {
        this.searchControl = this.formBuilder.control('');
    }

    // #region initSourceStreams
    private initSourceStreams(): void {
        this.searchTerm$ = this.buildSearchTermStream();
        this.selectedState$ = this.buildSelectedStateStream();
    }

    private buildSearchTermStream(): Observable<string> {
        if (this.search) {
            return (this.searchControl.valueChanges as Observable<string>).pipe(
                debounceTime(300),
                distinctUntilChanged(isEqual),
                startWith('')
            );
        } else {
            return of('');
        }
    }

    private initOptionsStream(): void {
        const latestPage$: Observable<PaginatedResults<T>> = combineLatest([this.currentPage$, this.searchTerm$]).pipe(
            debounceTime(300),
            tap(([page, searchTerm]) => {
                if (page === 0) {
                    this.loading$.next(true);
                }
            }),
            switchMap(([page, searchTerm]) =>
                this.paginatedResultsFct(searchTerm, {
                    page,
                    resultsPerPage: this.resultsPerPage
                })
            ),
            shareReplay()
        );
        this.showExtraItemsLabelIndicator$ = latestPage$.pipe(
            map(
                paginatedResults =>
                    paginatedResults.paginationInfo.totalNumberOfResults >
                    (paginatedResults.paginationInfo.page + 1) * paginatedResults.paginationInfo.resultsPerPage
            )
        );

        latestPage$.subscribe(latestPage => {
            const formSelectOptions: FormSelectOption<T>[] = latestPage.results.map(value => ({
                label: this.valueToLabelFct(value),
                value
            }));

            if (latestPage.paginationInfo.page === 0) {
                // Reset the options
                this.options$.next(formSelectOptions);
            } else {
                const updatedOptions: FormSelectOption<T>[] = [...this.options$.value, ...formSelectOptions];

                this.options$.next(updatedOptions);
            }

            this.loading$.next(false);
        });
    }

    private buildSelectedStateStream(): Observable<FormValue<FormSelectOption<T>>[]> {
        return this.state$.pipe(
            map(formValues => formValues.filter(formValue => formValue.state !== FormValueState.UNSET))
        );
    }
    // #endregion

    private initStateStreams(): void {
        this.selectedValues$ = this.state$.pipe(
            map(input => input.filter(formValue => formValue.state === FormValueState.SET)),
            map(input => input.map(formValue => formValue.value.value))
        );
    }

    private initMessageStream(): void {
        this.selectLabel$ = this.selectedValues$.pipe(
            map(selected => {
                if (selected.length === 0) {
                    return this.placeholderMsg;
                }
                if (selected.length === 1) {
                    return this.valueToLabelFct(selected[0]);
                }
                return this.translateService.translate('general.filter.multipleSelected');
            })
        );
    }

    private initListeners(): void {
        this.initSearchListener();
        this.initInputListener();
        this.initDeletePressListener();
        this.initSearchFocusListener();
        this.initOptionsListener();
        this.initOpenListener();
    }

    // #region initListeners
    private initSearchListener(): void {
        // Reset pagination every time search changes
        this.searchTerm$.subscribe(_ => this.resetPage());
    }

    private initInputListener(): void {
        this.initialSelectedValues$
            .pipe(
                withLatestFrom(this.multiFormSelectOptions$.pipe(startWith({ isLiveData: true, options: [] }))),
                takeUntil(this.destroy$)
            )
            .subscribe(([formValues, multiFormSelectOptions]) => {
                this.setState(this.setOptionSelected(formValues, multiFormSelectOptions));
            });

        // Once the options are loaded, check if any labels needs updating
        this.multiFormSelectOptions$
            .pipe(takeUntil(this.destroy$))
            .subscribe(options => this.updateStateWithOptions(options.options));
    }

    private updateStateWithOptions(options: FormSelectOption<T>[]): void {
        this.state$.pipe(take(1), takeUntil(this.destroy$)).subscribe(state => {
            const newState: FormValue<FormSelectOption<T>>[] = state.map(formValue => {
                if (formValue.value.value !== '') {
                    return formValue;
                }

                const formSelectOption: FormSelectOption<T> | undefined = options.find(option =>
                    isEqual(option.value, formValue.value.value)
                );
                if (formSelectOption) {
                    return new FormValue(formSelectOption, formValue.state);
                }

                return formValue;
            });

            this.setState(newState);
        });
    }

    private setOptionSelected(
        formValues: FormValue<T>[],
        multiFormSelectOptions: MultiFormSelectOptions<T>
    ): FormValue<FormSelectOption<T>>[] {
        // Base selection, based on the available values in multiFormSelectOptions
        const options: FormValue<FormSelectOption<T>>[] = multiFormSelectOptions.options.map(formSelectOption => {
            const value: T = formSelectOption.value;

            const formValue: FormValue<T> | undefined = formValues.find(fv => isEqual(fv.value, value));

            const state: FormValueState = formValue ? formValue.state : FormValueState.UNSET;
            return new FormValue(formSelectOption, state);
        });

        /* When multiFormSelectOptions are live, the contents do not represent all selectable values,
        but can be dependent on e.g. search query, pagination,...
            Allow selection of any random value. It is up to the wrapper code to make sure it's a valid value.
         */
        if (multiFormSelectOptions.isLiveData === true) {
            const extraSelected: FormValue<T>[] = formValues.filter(formvalue =>
                options.every(option => !isEqual(option.value.value, formvalue.value))
            );

            const extraState: FormValue<FormSelectOption<T>>[] = extraSelected.map(
                formValue =>
                    new FormValue(
                        { label: this.estimateLabelOfMissingValue(formValue.value), value: formValue.value },
                        formValue.state
                    )
            );
            options.push(...extraState);
        }

        return options;
    }

    // Estimate label for values that are not present in provided options
    // Functions can be manually provided via valueToLabelFct to translate objects
    private estimateLabelOfMissingValue(value: T): string {
        if (typeof value === 'string') {
            return value;
        } else if (!!this.valueToLabelFct) {
            return this.valueToLabelFct(value);
        }

        return '';
    }

    private initStateChangeListener(): void {
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map(state => state.filter(objectState => objectState.state === FormValueState.SET)),
                map(state => state.map(selected => selected.value.value))
            )
            .subscribe(state => this.selectionState.emit(state));
    }

    private initDeletePressListener(): void {
        this.deletePress$
            .pipe(
                withLatestFrom(this.state$),
                map(([_, state]) => state),
                filter(state => state.length > 0),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.unsetAllOptions();
            });
    }

    private initSearchFocusListener(): void {
        this.isOpen$
            .pipe(
                filter(() => !!this.search),
                filter(isOpen => !!isOpen),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.focusOnSearch();
            });
    }

    private initOpenListener(): void {
        this.isOpen$.pipe().subscribe(isOpen => {
            if (isOpen) {
                this.arrowNavigation.resetFocus();
                this.activate();
            } else if (this.isActiveComponent()) {
                this.deactivate();
            }
        });
    }

    private initOptionsListener(): void {
        this.multiFormSelectOptions$
            .pipe(distinctUntilChanged(isEqual), withLatestFrom(this.state$), takeUntil(this.destroy$))
            .subscribe(([options, state]) => {
                const newState: FormValue<FormSelectOption<T>>[] = state;
                let modified = false;

                options.options.forEach((option: FormSelectOption<T>) => {
                    const index: number = newState.findIndex((state: FormValue<FormSelectOption<T>>) =>
                        isEqual(state.value.value, option.value)
                    );

                    if (index > -1) {
                        newState[index] = new FormValue(option, newState[index].state);
                        modified = true;
                    }
                });

                if (modified) {
                    this.setState(newState);
                }
            });
    }
    // #endregion

    private initHasOptionsStream(): void {
        this.hasOptions$ = this.options$.pipe(
            take(1),
            map(() => true)
        );
    }

    // #region Search
    private focusOnSearch(): void {
        setTimeout(() => this.searchElement?.nativeElement.focus(), 0);
    }

    private resetSearch(): void {
        if (this.search) {
            this.searchControl.reset('');
        }
    }

    private resetPage(): void {
        this.currentPage$.next(0);
    }
    // #endregion

    // #region State
    private setState(newState: FormValue<FormSelectOption<T>>[]): void {
        this.state$.next(newState);
    }

    public getState(formSelectOption: FormSelectOption<T>): FormValueState {
        const formValue: FormValue<FormSelectOption<T>> | undefined = this.state$.value.find(fv =>
            isEqual(fv.value.value, formSelectOption.value)
        );

        if (typeof formValue === 'undefined') {
            return FormValueState.UNSET;
        }
        return formValue.state;
    }

    public isIndeterminate(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.INDETERMINATE;
    }

    public isUnset(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.UNSET;
    }

    public isSet(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.SET;
    }

    // #endregion

    // #region Option helpers
    private unsetAllOptions(): void {
        const newState: FormValue<FormSelectOption<T>>[] = this.state$.value.map(
            formValue => new FormValue(formValue.value, FormValueState.UNSET)
        );
        this.setState(newState);
    }

    public toggleCheckbox(event: Event, selectedOption: FormSelectOption<T>): void {
        let target: HTMLElement | null = event.target as HTMLElement | null;
        if (target && target?.tagName !== 'INPUT') {
            target = target.querySelector('input');
            (target as HTMLInputElement).checked = !(target as HTMLInputElement).checked;
        }
        const isChecked: boolean = (target as HTMLInputElement).checked;

        if (isChecked && selectedOption.disabled) {
            return;
        }

        const state: FormValueState = isChecked ? FormValueState.SET : FormValueState.UNSET;

        const currentValue: FormValue<FormSelectOption<T>>[] = [...this.state$.value];
        const index: number = currentValue.findIndex(fv => isEqual(fv.value.value, selectedOption.value));

        if (index > -1) {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(currentValue[index].value, state);
            currentValue.splice(index, 1, formValue);
        } else {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(selectedOption, FormValueState.SET);
            currentValue.push(formValue);
        }

        this.resetSearch();

        this.setState(currentValue);
    }

    public selectSingleValue(selectedOption: FormSelectOption<T>): void {
        if (selectedOption.disabled) {
            return;
        }

        this.resetSearch();

        if (this.isSet(selectedOption) && this.allowSingleDeselect) {
            this.unsetAllOptions();
            this.setDropdownClosed();
            return;
        }

        this.setState([new FormValue(selectedOption)]);
        this.setDropdownClosed();
    }
    // #endregion

    // #region Helpers
    public toggleDropdown(): void {
        this.isOpen$.next(!this.isOpen$.value);
    }

    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    public handleDeletePress(keyboardEvent: KeyboardEvent): void {
        if (this.DELETE_INPUT_KEYS.includes(keyboardEvent.key)) {
            this.deletePress$.next(keyboardEvent);
        }
    }

    public trackByFn(index: number, option: FormSelectOption<T>): T {
        return option.value;
    }

    public isDisabled(option: FormSelectOption, state: FormValueState): boolean {
        return !!option.disabled && (!option.allowUnsetDisabled || state !== this.states.SET);
    }

    private loadNextPage() {
        this.currentPage$.next(this.currentPage$.value + 1);
    }

    public get loadMoreActionItem(): ActionItem {
        return {
            label: this.showMoreMsg,
            style: ActionItemStyle.Link,
            modifiers: [ActionItemModifier.Bold],
            eventListener: () => this.loadNextPage()
        };
    }

    public onSearchFocus(): void {
        this.arrowNavigation.onSearchFocus();
    }

    public get focusIndex(): number {
        return this.arrowNavigation?.focusIndex ?? -1;
    }

    public onHover(event: MouseEvent): void {
        this.arrowNavigation.onHover(event.target as HTMLElement, this.optionContainerElement);
    }
    // #endregion
}
